import { useState, useEffect } from 'react';

const useIndexedDB = (dbName = 'restaurant_cart_db', storeName = 'carts') => {
    const [db, setDb] = useState(null);
    const [isInitialized, setIsInitialized] = useState(false);

    const initDB = async () => {
        try {
            const request = indexedDB.open(dbName, 1);
            
            request.onupgradeneeded = (event) => {
                const db = event.target.result;
                if (!db.objectStoreNames.contains(storeName)) {
                    db.createObjectStore(storeName, { keyPath: 'restaurantId' });
                }
            };

            request.onsuccess = (event) => {
                setDb(event.target.result);
                setIsInitialized(true);
            };

            request.onerror = (event) => {
                console.error('Error initializing IndexedDB:', event.target.error);
            };
        } catch (error) {
            console.error('Error in IndexedDB initialization:', error);
        }
    };

    useEffect(() => {
        initDB();
    }, [dbName, storeName]);

    const saveCart = async (restaurantId, cartData) => {
        if (!db || !isInitialized) {
            throw new Error('Database not initialized');
        }

        return new Promise((resolve, reject) => {
            try {
                const transaction = db.transaction(storeName, 'readwrite');
                const store = transaction.objectStore(storeName);
                
                const request = store.put({ 
                    restaurantId, 
                    cartData: {
                        ...cartData,
                        lastUpdated: new Date().getTime()
                    }
                });

                request.onsuccess = () => resolve(true);
                request.onerror = () => reject(request.error);
                
                transaction.oncomplete = () => resolve(true);
                transaction.onerror = () => reject(transaction.error);
            } catch (error) {
                reject(error);
            }
        });
    };

    const loadCart = async (restaurantId) => {
        if (!db || !isInitialized) {
            throw new Error('Database not initialized');
        }

        if (!restaurantId) {
            console.warn('No restaurantId provided to loadCart');
            return null;
        }

        return new Promise((resolve, reject) => {
            try {
                const transaction = db.transaction(storeName, 'readonly');
                const store = transaction.objectStore(storeName);
                const request = store.get(restaurantId);

                request.onsuccess = () => {
                    if (request.result) {
                        resolve(request.result.cartData);
                    } else {
                        resolve(null);
                    }
                };

                request.onerror = () => reject(request.error);
            } catch (error) {
                reject(error);
            }
        });
    };

    const deleteCart = async (restaurantId) => {
        if (!db || !isInitialized) {
            throw new Error('Database not initialized');
        }

        return new Promise((resolve, reject) => {
            try {
                const transaction = db.transaction(storeName, 'readwrite');
                const store = transaction.objectStore(storeName);
                const request = store.delete(restaurantId);

                request.onsuccess = () => resolve(true);
                request.onerror = () => reject(request.error);
            } catch (error) {
                reject(error);
            }
        });
    };

    const deleteAllDBs = async () => {
        const databases = await indexedDB.databases();
        const targetDB = databases.find((db) => db.name === 'restaurant_cart_db');

        if (targetDB) {
            localStorage.removeItem('restaurant_id_ref');
            const restaurantCartDB = databases.find((db) => db.name === 'restaurant_cart_db');
            if (restaurantCartDB) {
                const request = indexedDB.open(restaurantCartDB.name); // Ouvrir la base de données

                request.onsuccess = async (event) => {
                    const db = event.target.result; // Obtenir l'instance de la base de données
                    const transaction = db.transaction('carts', 'readwrite'); // Créer une transaction
                    const objectStore = transaction.objectStore('carts');

                    const allRecordsRequest = objectStore.getAll(); // Récupérer tous les enregistrements

                    allRecordsRequest.onsuccess = async (event) => {
                        const allRecords = event.target.result; // Obtenir les enregistrements

                        allRecords.forEach(record => {
                            objectStore.delete(record.restaurantId); // Supprimer chaque enregistrement
                        });
                    };

                    allRecordsRequest.onerror = (event) => {
                        console.error('Error fetching records:', event.target.error);
                    };
                };

                request.onerror = (event) => {
                    console.error('Error opening database:', event.target.error);
                };
            }
        }
    };

    return {
        initDB,
        saveCart,
        loadCart,
        isInitialized,
        deleteCart,
        deleteAllDBs
    };
};

export default useIndexedDB;